import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    VStack,
    Text,
    Spinner,
    Center,
    Field,
    HStack,
    Link,
    Input,
    createListCollection,
} from '@chakra-ui/react';
import { useTranslation } from '../../../../hooks/useTranslation';
import { useGetRegCustomerQuery, usePatchRegCustomerMutation } from '../../../../api';
import { PatchRegCustomer } from '../../../../api/registration/types';
import { getTariffDetailsUrl } from '../../../../api/constants';
import { alertManager } from '../../../../utils/alertManager';
import BaseSelect from '../../../../components/base/BaseSelect';
import Button from '../../../../components/base/Button';
import {
    REGISTRATION_OFFICES,
    getOfficeById,
    resolveRegistrationOfficeId,
} from '../../../../config/registrationOffices';
import { PUBLIC_TARIFFS, getTariffById, resolvePublicTariffId } from '../../../../config/tariffs';

type RegistrationStepProps = {
    onBack: () => void;
    onNext: () => void;
};

export const StepSelectTariff: React.FC<RegistrationStepProps> = ({ onBack, onNext }) => {
    const { t, currentLanguage } = useTranslation();
    const { data: regCustomer, isLoading } = useGetRegCustomerQuery();
    const [patchRegCustomer, { isLoading: isPatching }] = usePatchRegCustomerMutation();

    const [officeId, setOfficeId] = useState(String(REGISTRATION_OFFICES[0].id));
    const [tariffId, setTariffId] = useState(String(PUBLIC_TARIFFS[0].id));
    const [promoCode, setPromoCode] = useState('');
    const appliedFromApi = useRef(false);

    useEffect(() => {
        if (isLoading || !regCustomer) {
            return;
        }
        if (appliedFromApi.current) {
            return;
        }

        const { id: resolvedOfficeId } = resolveRegistrationOfficeId(regCustomer.office_id);
        const { id: resolvedTariffId } = resolvePublicTariffId(regCustomer.subscription_id);

        setOfficeId(resolvedOfficeId);
        setTariffId(resolvedTariffId);
        setPromoCode(regCustomer.promo_code ?? '');
        appliedFromApi.current = true;
    }, [isLoading, regCustomer]);

    const handleNextClick = async () => {
        const patchPayload: PatchRegCustomer = {
            office_id: Number(officeId),
            subscription_id: Number(tariffId),
            language: currentLanguage,
            promo_code: promoCode.trim(),
        };

        try {
            await patchRegCustomer(patchPayload).unwrap();
        } catch {
            alertManager.error(t.registration.errors.serverError);
            return;
        }

        onNext();
    };

    const officeCollection = useMemo(
        () =>
            createListCollection({
                items: REGISTRATION_OFFICES.map((o) => ({
                    value: String(o.id),
                    label: t.registration.form[o.nameKey],
                })),
            }),
        [t],
    );

    const tariffCollection = useMemo(
        () =>
            createListCollection({
                items: PUBLIC_TARIFFS.map((tariff) => ({
                    value: String(tariff.id),
                    label: tariff.name,
                })),
            }),
        [],
    );

    const selectedOffice = getOfficeById(officeId);
    const selectedTariff = getTariffById(Number(tariffId));
    const tariffDetailsUrl = getTariffDetailsUrl(currentLanguage);

    if (isLoading) {
        return (
            <Center py={10} flexDirection="column" gap={4}>
                <Spinner size="lg" color="yellow.600" />
                <Text fontSize="md" color="gray.700" textAlign="center">
                    {t.registration.form.loadingRegCustomerData}
                </Text>
            </Center>
        );
    }

    return (
        <VStack align="stretch" w="full" h="full" minH={0}>
            <VStack
                gap={6}
                align="stretch"
                flex={1}
                minH={0}
                overflowY="auto"
                p={1}
                css={{
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                }}
            >
                <Field.Root>
                    <Field.Label fontSize="md" fontWeight="bold" color="gray.800" mb={2}>
                        {t.registration.form.selectOfficeLabel}
                    </Field.Label>
                    <BaseSelect
                        collection={officeCollection}
                        value={officeId}
                        onValueChange={setOfficeId}
                        variant="white"
                        w="full"
                        disabled={isPatching}
                    />
                    {selectedOffice && (
                        <Text fontSize="sm" color="gray.600" mt={2} lineHeight="short">
                            {selectedOffice.address}
                        </Text>
                    )}
                </Field.Root>

                <Field.Root>
                    <Field.Label fontSize="md" fontWeight="bold" color="gray.800" mb={2}>
                        {t.registration.form.selectTariffLabel}
                    </Field.Label>
                    <BaseSelect
                        collection={tariffCollection}
                        value={tariffId}
                        onValueChange={setTariffId}
                        variant="white"
                        w="full"
                        disabled={isPatching}
                    />
                    {selectedTariff && (
                        <VStack align="stretch" gap={1}>
                            <Text fontSize="sm" color="gray.600">
                                {t.pages.payment.fromPrice} €{selectedTariff.price} {t.pages.payment.perMonth}
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                                {t.pages.payment.withoutVat}
                            </Text>
                            <Link
                                href={tariffDetailsUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                fontSize="md"
                                color="blue.600"
                                textDecoration="underline"
                                _focus={{ outline: 'none' }}
                            >
                                {t.registration.form.tariffDetailsLink}
                            </Link>
                        </VStack>
                    )}
                </Field.Root>

                <Field.Root>
                    <Field.Label fontSize="md" fontWeight="bold" color="gray.800" mb={2}>
                        {t.registration.form.promoCodeLabel} ({t.registration.form.ifAvailable})
                    </Field.Label>
                    <Input
                        type="text"
                        value={promoCode}
                        onChange={(e) => setPromoCode(e.target.value)}
                        placeholder={t.registration.form.promoCodePlaceholder}
                        size="lg"
                        h="48px"
                        borderRadius="0"
                        disabled={isPatching}
                    />
                </Field.Root>
            </VStack>

            <HStack gap={3} justify="space-between" w="full" flexShrink={0} pt={2}>
                <Button buttonType="secondary" size="lg" flex={1} onClick={onBack}>
                    {t.registration.form.back}
                </Button>
                <Button
                    buttonType="success"
                    size="lg"
                    flex={1}
                    isLoading={isPatching}
                    onClick={handleNextClick}
                >
                    {t.registration.form.next}
                </Button>
            </HStack>
        </VStack>
    );
};
